import { type FormEvent, memo, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button, SelectButtonsNew } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useBorrowers } from '@/hooks/use-borrowers';
import { useUpdateLoan } from '@/hooks/use-loans';
import { useReferrers } from '@/hooks/use-referrers';
import { handleMutationError } from '@/lib/error-handling';
import {
  getFrequencyLabel,
  getPrincipalHelperText,
  getPrincipalLabel,
  isLineOfCredit,
} from '@/lib/loan-type-labels';
import { toast } from '@/stores/toast-store';
import type { Loan, LoanType } from '@/types';

const LOAN_TYPES: { value: LoanType; label: string }[] = [
  { value: 'PERSONAL', label: 'Personal' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'MORTGAGE', label: 'Mortgage' },
  { value: 'AUTO', label: 'Auto' },
  { value: 'LINE_OF_CREDIT', label: 'Line of Credit' },
];

const FREQUENCIES = [
  { value: 'MONTHLY', label: 'Monthly' },
  { value: 'BIWEEKLY', label: 'Bi-weekly' },
  { value: 'WEEKLY', label: 'Weekly' },
  { value: 'QUARTERLY', label: 'Quarterly' },
];

interface Props {
  loan: Loan;
  open: boolean;
  onClose: () => void;
}

export const EditLoanModal = memo(function EditLoanModal({ loan, open, onClose }: Props) {
  const { data: borrowers } = useBorrowers();
  const { data: referrers } = useReferrers({ isActive: true });
  const mutation = useUpdateLoan();
  const borrowerOptions = borrowers?.data ?? [];
  const borrowerExistsInOptions = borrowerOptions.some(borrower => borrower.id === loan.borrowerId);
  const visibleBorrowers =
    borrowerExistsInOptions || !loan.borrowerId
      ? borrowerOptions
      : [
          {
            id: loan.borrowerId,
            name: loan.borrower?.name ?? `Current borrower (${loan.loanNumber})`,
          },
          ...borrowerOptions,
        ];

  const [borrowerId, setBorrowerId] = useState('');
  const [loanType, setLoanType] = useState<LoanType>('PERSONAL');
  const [principal, setPrincipal] = useState('');
  const [interestRate, setInterestRate] = useState('');
  const [defaultRate, setDefaultRate] = useState('');
  const [interestMethodEdit, setInterestMethodEdit] = useState<'PERCENTAGE' | 'FLAT_AMOUNT'>(
    'PERCENTAGE',
  );
  const [flatInterestAmount, setFlatInterestAmount] = useState('');
  const [termMonths, setTermMonths] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('MONTHLY');
  const [establishmentFee, setEstablishmentFee] = useState('');
  const [brokerageFee, setBrokerageFee] = useState('');
  const [legalCosts, setLegalCosts] = useState('');
  const [legalCostsRecovered, setLegalCostsRecovered] = useState(false);
  const [otherCosts, setOtherCosts] = useState('');
  const [otherCostsDescription, setOtherCostsDescription] = useState('');
  const [settlementDate, setSettlementDate] = useState('');
  const [showFees, setShowFees] = useState(false);
  // Loan references
  const [internalReference, setInternalReference] = useState('');
  const [lawFirmReference, setLawFirmReference] = useState('');
  const [externalReference, setExternalReference] = useState('');
  // Fee split & referrer
  const [referrerId, setReferrerId] = useState('');
  const [establishmentFeeRetained, setEstablishmentFeeRetained] = useState('');
  const [brokerageFeeRetained, setBrokerageFeeRetained] = useState('');
  const [referrerCommission, setReferrerCommission] = useState('');
  const isLoc = isLineOfCredit(loanType);

  // Populate form from loan data when modal opens or loan changes
  useEffect(() => {
    if (open && loan) {
      // Reset all state when loan ID changes
      setBorrowerId(loan.borrowerId);
      setLoanType(loan.loanType);
      setPrincipal(loan.principal);
      setInterestRate(loan.interestRate);
      setDefaultRate(loan.defaultRate ?? '');
      setInterestMethodEdit(loan.interestMethod ?? 'PERCENTAGE');
      setFlatInterestAmount(loan.flatInterestAmount ?? '');
      setTermMonths(String(loan.termMonths));
      setPaymentFrequency(loan.paymentFrequency ?? 'MONTHLY');
      setEstablishmentFee(loan.establishmentFee ?? '');
      setBrokerageFee(loan.brokerageFee ?? '');
      setLegalCosts(loan.legalCosts ?? '');
      setLegalCostsRecovered(loan.legalCostsRecovered ?? false);
      setOtherCosts(loan.otherCosts ?? '');
      setOtherCostsDescription(loan.otherCostsDescription ?? '');
      setSettlementDate(loan.settlementDate ? loan.settlementDate.split('T')[0] : '');
      // Loan references
      setInternalReference(loan.internalReference ?? '');
      setLawFirmReference(loan.lawFirmReference ?? '');
      setExternalReference((loan as any).externalReference ?? '');
      // Fee split & referrer
      setReferrerId(loan.referrerId ?? '');
      setEstablishmentFeeRetained(loan.establishmentFeeRetained ?? '');
      setBrokerageFeeRetained(loan.brokerageFeeRetained ?? '');
      setReferrerCommission(loan.referrerCommission ?? '');
      // Show fees section if any fees exist
      setShowFees(
        !!(loan.establishmentFee || loan.brokerageFee || loan.legalCosts || loan.otherCosts),
      );
    } else if (!open) {
      // Clear form state when modal closes
      setBorrowerId('');
      setLoanType('PERSONAL');
      setPrincipal('');
      setInterestRate('');
      setDefaultRate('');
      setInterestMethodEdit('PERCENTAGE');
      setFlatInterestAmount('');
      setTermMonths('');
      setPaymentFrequency('MONTHLY');
      setEstablishmentFee('');
      setBrokerageFee('');
      setLegalCosts('');
      setLegalCostsRecovered(false);
      setOtherCosts('');
      setOtherCostsDescription('');
      setSettlementDate('');
      setInternalReference('');
      setLawFirmReference('');
      setReferrerId('');
      setEstablishmentFeeRetained('');
      setBrokerageFeeRetained('');
      setReferrerCommission('');
      setShowFees(false);
    }
  }, [open, loan.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isLoc && interestMethodEdit !== 'PERCENTAGE') {
      setInterestMethodEdit('PERCENTAGE');
      setFlatInterestAmount('');
    }
  }, [interestMethodEdit, isLoc]);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    mutation.mutate(
      {
        loanId: loan.id,
        borrowerId,
        loanType,
        principal: parseFloat(principal),
        interestRate: interestMethodEdit === 'FLAT_AMOUNT' ? 0 : parseFloat(interestRate),
        defaultRate: defaultRate ? parseFloat(defaultRate) : null,
        interestMethod: interestMethodEdit,
        flatInterestAmount:
          interestMethodEdit === 'FLAT_AMOUNT' && flatInterestAmount
            ? parseFloat(flatInterestAmount)
            : null,
        termMonths: parseInt(termMonths, 10),
        paymentFrequency: paymentFrequency as any,
        settlementDate: settlementDate || null,
        establishmentFee: establishmentFee ? parseFloat(establishmentFee) : null,
        brokerageFee: brokerageFee ? parseFloat(brokerageFee) : null,
        legalCosts: legalCosts ? parseFloat(legalCosts) : null,
        legalCostsRecovered,
        otherCosts: otherCosts ? parseFloat(otherCosts) : null,
        otherCostsDescription: otherCostsDescription || null,
        internalReference: internalReference || null,
        lawFirmReference: lawFirmReference || null,
        externalReference: externalReference || null,
        referrerId: referrerId || null,
        establishmentFeeRetained: establishmentFeeRetained
          ? parseFloat(establishmentFeeRetained)
          : null,
        brokerageFeeRetained: brokerageFeeRetained ? parseFloat(brokerageFeeRetained) : null,
        referrerCommission: referrerCommission ? parseFloat(referrerCommission) : null,
      },
      {
        onSuccess: () => {
          toast.success('Loan updated');
          onClose();
        },
        onError: (err: any) => handleMutationError(err, 'Failed to update loan'),
      },
    );
  }

  if (!open) return null;

  return (
    <ModalContentWrapper open={open} onClose={onClose} title={`Edit Loan — ${loan.loanNumber}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Borrower */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Borrower</label>
          <select
            required
            value={borrowerId}
            onChange={e => setBorrowerId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          >
            <option value="">Select borrower…</option>
            {visibleBorrowers.map((b: { id: string; name: string }) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>

        {/* Loan type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Loan Type</label>
          <select
            value={loanType}
            onChange={e => setLoanType(e.target.value as LoanType)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          >
            {LOAN_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* Interest Method Toggle */}
        {isLoc ? (
          <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
            Line of credit uses a percentage rate and accrues interest on the drawn balance.
          </div>
        ) : (
          <div className="flex items-center gap-3 mb-1">
            <span className="text-sm font-medium text-gray-700">Interest:</span>
            <SelectButtonsNew
              value={interestMethodEdit}
              onChange={setInterestMethodEdit}
              options={[
                { value: 'PERCENTAGE', label: '% Rate' },
                { value: 'FLAT_AMOUNT', label: '$ Fixed' },
              ]}
              classes={{
                container: 'w-auto',
                buttonsContainer: 'min-h-0 bg-white border-gray-200',
                button: 'px-2.5 py-1 text-xs font-medium',
              }}
              capitalize={false}
            />
          </div>
        )}

        {/* Principal + Interest + Default Rate */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getPrincipalLabel(loanType)}
            </label>
            <input
              type="number"
              required
              min="1"
              step="0.01"
              value={principal}
              onChange={e => setPrincipal(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
            {getPrincipalHelperText(loanType) && (
              <p className="mt-1 text-xs text-gray-500">{getPrincipalHelperText(loanType)}</p>
            )}
          </div>
          {interestMethodEdit === 'PERCENTAGE' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Interest Rate (%)
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={interestRate}
                onChange={e => setInterestRate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Total Interest ($)
              </label>
              <input
                type="number"
                required
                min="0"
                step="0.01"
                value={flatInterestAmount}
                onChange={e => setFlatInterestAmount(e.target.value)}
                placeholder="e.g. 5000"
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate (%)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={defaultRate}
              onChange={e => setDefaultRate(e.target.value)}
              placeholder="—"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Term + Frequency + Settlement Date */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Term (months)</label>
            <input
              type="number"
              required
              min="1"
              value={termMonths}
              onChange={e => setTermMonths(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getFrequencyLabel(loanType)}
            </label>
            <select
              value={paymentFrequency}
              onChange={e => setPaymentFrequency(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            >
              {FREQUENCIES.map(f => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Date</label>
            <input
              type="date"
              value={settlementDate}
              onChange={e => setSettlementDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Referrer / Broker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Referrer / Broker</label>
          <select
            value={referrerId}
            onChange={e => setReferrerId(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                         focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
          >
            <option value="">No referrer</option>
            {referrers?.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}
                {r.company ? ` (${r.company})` : ''}
                {r.commissionRate ? ` — ${r.commissionRate}%` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Loan References */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Internal Reference
            </label>
            <input
              value={internalReference}
              onChange={e => setInternalReference(e.target.value)}
              placeholder="e.g. LOAN-2024-001"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Law Firm Reference
            </label>
            <input
              value={lawFirmReference}
              onChange={e => setLawFirmReference(e.target.value)}
              placeholder="e.g. Summers ref"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Document Loan ID
              <span className="text-xs text-gray-400 ml-1">
                (shown on Offer Letters & statements — e.g. Summers trust account number)
              </span>
            </label>
            <input
              value={externalReference}
              onChange={e => setExternalReference(e.target.value)}
              placeholder="e.g. LOAN-2026-0001 or trust account number"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                           placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                           focus:ring-blue-500 outline-none"
            />
          </div>
        </div>

        {/* Fees toggle */}
        <button
          type="button"
          onClick={() => setShowFees(!showFees)}
          className="text-sm text-indigo-600 hover:text-indigo-700 font-medium"
        >
          {showFees ? '− Hide' : '+ Show'} Establishment Costs & Fees
        </button>

        {showFees && (
          <div className="space-y-3 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Establishment Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={establishmentFee}
                  onChange={e => setEstablishmentFee(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Brokerage Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={brokerageFee}
                  onChange={e => setBrokerageFee(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Legal Costs ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={legalCosts}
                  onChange={e => setLegalCosts(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={legalCostsRecovered}
                    onChange={e => setLegalCostsRecovered(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-blue-500"
                  />
                  Legal costs recovered
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Other Costs ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={otherCosts}
                  onChange={e => setOtherCosts(e.target.value)}
                  placeholder="0.00"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  value={otherCostsDescription}
                  onChange={e => setOtherCostsDescription(e.target.value)}
                  placeholder="e.g. Valuation fees"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                               placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                               focus:ring-blue-500 outline-none"
                />
              </div>
            </div>

            {/* Fee split - lender retained */}
            <div className="border-t border-gray-200 pt-3 mt-3">
              <p className="text-xs font-medium text-gray-500 mb-3">
                Lender Fee Split - What We Keep
              </p>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Establishment Retained ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={establishmentFeeRetained}
                    onChange={e => setEstablishmentFeeRetained(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                                 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brokerage Retained ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={brokerageFeeRetained}
                    onChange={e => setBrokerageFeeRetained(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                                 focus:ring-blue-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Referrer Commission ($)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={referrerCommission}
                    onChange={e => setReferrerCommission(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                 placeholder:text-gray-400 focus:border-blue-500 focus:ring-1
                                 focus:ring-blue-500 outline-none"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error */}
        {mutation.isError && (
          <p className="text-sm text-red-600">
            {(() => {
              const msg = (mutation.error as any)?.response?.data?.message;
              if (Array.isArray(msg)) return msg.join(', ');
              return msg ?? 'Failed to update loan';
            })()}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={mutation.isPending}
            className="w-fit flex items-center gap-2"
          >
            {mutation.isPending && <Spinner size="lg" className="w-4 h-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </form>
    </ModalContentWrapper>
  );
});
